"use client";

import AppImage from "@/components/ui/AppImage";
import { useLanguage } from "@/contexts/LanguageContext";

export default function HeroSection() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/assets/images/Bildschirmfoto_2026-02-21_um_11.09.49-1771685790066.png"
          alt="HÅR Extension Salon Interior"
          width={1920}
          height={1080}
          className="w-full h-full object-cover"
        />
        {/* Dark overlay for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/60 to-charcoal/75" />
      </div>

      {/* Subtle Pattern Overlay */}
      <div className="absolute inset-0 z-[1] opacity-[0.02]">
        <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FFFFFF" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 md:px-12 py-40 text-center">
        {/* Logo */}
        <div className="flex justify-center mb-12 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <AppImage
            src="/assets/images/ChatGPT_Image_21._Feb._2026_11_41_01-1771661568738.png"
            alt="HÅR Extension Logo"
            width={1600}
            height={800}
            className="h-40 w-auto object-contain drop-shadow-2xl"
          />
        </div>

        {/* Headline */}
        <h1
          className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-wide leading-[1.15] text-white mb-8 animate-fade-up drop-shadow-lg"
          style={{ animationDelay: "0.2s", letterSpacing: '0.02em' }}
        >
          <span className="block tracking-wider" style={{ letterSpacing: '0.08em', fontWeight: 300 }}>
            {t('hero.headline1')}
          </span>
          <span className="block italic text-gold mt-3 tracking-wide" style={{ letterSpacing: '0.05em', fontWeight: 300 }}>
            {t('hero.headline2')}
          </span>
        </h1>

        <p
          className="text-lg md:text-xl text-cream font-light leading-relaxed max-w-3xl mx-auto mb-14 animate-fade-up drop-shadow-md"
          style={{ animationDelay: "0.35s" }}
        >
          {t('hero.subtitle')}
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row gap-5 justify-center animate-fade-up"
          style={{ animationDelay: "0.5s" }}
        >
          <a href="https://wa.me/971585675600" target="_blank" rel="noopener noreferrer" className="btn-primary shadow-luxury-lg">
            Book Appointment
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </a>
          <a href="https://wa.me/971585675600" target="_blank" rel="noopener noreferrer" className="btn-whatsapp shadow-luxury-lg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t('hero.whatsappUs')}
          </a>
        </div>

        {/* Contact Info */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-cream animate-fade-up" style={{ animationDelay: "0.65s" }}>
          <a href="mailto:info@harextension.com" className="flex items-center gap-2.5 hover:text-gold transition-colors drop-shadow-md">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            {t('hero.email')}
          </a>
          <span className="hidden sm:block w-1.5 h-1.5 rounded-full bg-cream/50" />
          <span className="flex items-center gap-2.5 drop-shadow-md">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {t('hero.location')}
          </span>
        </div>
      </div>
    </section>
  );
}